// src/pages/creator-panel/LivePhoneCall/LivePhoneCall.js
// Per-chatbot configuration page for the real-telephony channel.
// Mirrors LiveConversation.js (RAG toggle, greeting, prompt, sources,
// manual content, variables) and adds telephony-specific fields:
//   - Phone provider selection (from shared SmsServiceConfig records
//     whose `channels` include 'phone')
//   - Inbound phone number (E.164)
//   - Outbound calls toggle
//   - Manage Tools button (routes to the shared Tool Call page at
//     /tool-call — tools are a single shared pool with three channel toggles)
import React, { useState, useEffect, useCallback } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';
import { hasPermission } from '../../../utils/permissionUtils';
import TiptapEditor from '../../../components/Common/TiptapEditor';
import {
    Box, Typography, Alert, Button, Switch, FormControlLabel, TextField, CircularProgress,
    Card, CardContent, Tabs, Tab, Checkbox, LinearProgress, Chip, IconButton,
    Divider, InputAdornment, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
    Save as SaveIcon, Language as WebIcon, Description as DocIcon, CloudQueue as DriveIcon,
    Search as SearchIcon, Delete as DeleteIcon,
    Build as ToolIcon, Code as VarIcon, Phone as PhoneIcon, AddCircleOutline as AddIcon,
    FiberManualRecord as RecordIcon, SettingsPhone as ProviderIcon, ReceiptLong as LogsIcon,
    ArrowForward as NextIcon, ArrowBack as BackIcon, CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const MAX_CHARACTERS = 20000;
const STEP_LABELS = ['Telephony', 'General', 'Knowledge & sources', 'Variables'];

// Cold-transfer working hours: per-weekday open window, evaluated in the chosen
// timezone. Default timezone = Central Time (CT). Default schedule = Mon–Fri
// 09:00–17:00, weekend off.
const DEFAULT_TRANSFER_TIMEZONE = 'America/Chicago';
const WEEKDAYS = [
    ['monday', 'Monday'], ['tuesday', 'Tuesday'], ['wednesday', 'Wednesday'],
    ['thursday', 'Thursday'], ['friday', 'Friday'], ['saturday', 'Saturday'], ['sunday', 'Sunday'],
];
const DEFAULT_WORKING_HOURS = WEEKDAYS.reduce((acc, [key], i) => {
    acc[key] = { enabled: i < 5, start: '09:00', end: '17:00' }; // Mon–Fri on
    return acc;
}, {});

// Always return all 7 days as fresh objects, merging any stored partial map.
const normalizeWorkingHours = (raw) => {
    const out = {};
    WEEKDAYS.forEach(([key]) => {
        const d = (raw && typeof raw === 'object' && raw[key]) || DEFAULT_WORKING_HOURS[key];
        out[key] = { enabled: !!d.enabled, start: d.start || '09:00', end: d.end || '17:00' };
    });
    return out;
};

export default function LivePhoneCall() {
    const navigate = useNavigate();
    const { chatbotId: urlChatbotId } = useParams();
    const { activeChatbot } = useChatbots();
    // The dashboard's chatbot-scoped layout lives at `/chatbot/:chatbotId/*`,
    // so every navigate() needs the same prefix. Prefer URL param, fall back
    // to the context's active chatbot.
    const chatbotId = urlChatbotId || activeChatbot?.chatbot_identity;
    const scope = `/chatbot/${chatbotId}`;

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Active setup step / tab (0 = Telephony, 1 = General, 2 = Knowledge, 3 = Variables)
    const [tab, setTab] = useState(0);

    // Same shape as LiveConversation
    const [ragEnabled, setRagEnabled] = useState(true);
    const [greetingMessage, setGreetingMessage] = useState('Hello, how can I help you?');
    const [promptText, setPromptText] = useState('');
    const [selectedSources, setSelectedSources] = useState([]);
    const [variableRows, setVariableRows] = useState([]);
    const [availableSources, setAvailableSources] = useState([]);
    const [sourceTab, setSourceTab] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadingSources, setLoadingSources] = useState(false);
    const [message, setMessage] = useState(null);

    // Phone-specific fields
    const [phoneProviders, setPhoneProviders] = useState([]);
    // Distinguishes "not fetched yet" from "fetched, none configured" so the
    // provider-setup gate below doesn't flash before the providers load.
    const [providersLoaded, setProvidersLoaded] = useState(false);
    const [serviceConfigId, setServiceConfigId] = useState('');
    const [inboundNumber, setInboundNumber] = useState('');
    const [outboundEnabled, setOutboundEnabled] = useState(false);

    // Cold-transfer (human handoff): when the AI can't answer — or the caller
    // asks for a human — the live call is redirected to this real number.
    const [transferEnabled, setTransferEnabled] = useState(false);
    const [transferNumber, setTransferNumber] = useState('');
    // Working-hours gate for the transfer + the timezone it's evaluated in.
    const [transferTimezone, setTransferTimezone] = useState(DEFAULT_TRANSFER_TIMEZONE);
    const [transferWorkingHours, setTransferWorkingHours] = useState(() => normalizeWorkingHours(null));
    const [timezones, setTimezones] = useState([]);

    // Per-chatbot voice provider ('' = use the klonit .env default).
    const [sttProvider, setSttProvider] = useState('');

    const sourceTypes = ['website', 'document', 'gdrive'];
    const sourceTabLabels = ['Website', 'Documents', 'Google Drive'];
    const sourceTabIcons = [<WebIcon />, <DocIcon />, <DriveIcon />];

    // Manual content editor (Tiptap) — same as Live Conversation
    const [manualContent, setManualContent] = useState('');
    const manualEditor = useEditor({
        extensions: [StarterKit, Underline],
        content: '',
        onUpdate: ({ editor }) => setManualContent(editor.getHTML()),
    });

    useEffect(() => {
        let active = true;
        (async () => {
            const v = await hasPermission('live-phone-call', 'view');
            const a = await hasPermission('live-phone-call', 'add');
            const e = await hasPermission('live-phone-call', 'edit');
            if (!active) return;
            setCanView(v);
            setCanModify(a || e);
            setCheckingPermissions(false);
        })();
        return () => { active = false; };
    }, []);

    const computeTotalChars = useCallback(() => {
        const manualLen = manualContent ? manualContent.replace(/<[^>]+>/g, '').length : 0;
        return selectedSources.reduce((sum, s) => sum + (s.character_count || 0), manualLen);
    }, [manualContent, selectedSources]);

    const fetchSettings = useCallback(async () => {
        if (!chatbotId) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/chatbots/${chatbotId}/live-phone-call`);
            const data = res?.data?.data;
            if (data) {
                setRagEnabled(!!data.rag_enabled);
                setGreetingMessage(data.greeting_message || 'Hello, how can I help you?');
                setPromptText(data.prompt_text || '');
                setManualContent(data.manual_content || '');
                manualEditor?.commands?.setContent(data.manual_content || '');
                setSelectedSources(data.sources || []);
                setServiceConfigId(data.service_config_id || '');
                setInboundNumber(data.inbound_phone_number || '');
                setOutboundEnabled(!!data.outbound_enabled);
                setTransferEnabled(!!data.transfer_enabled);
                setTransferNumber(data.transfer_phone_number || '');
                setTransferTimezone(data.transfer_timezone || DEFAULT_TRANSFER_TIMEZONE);
                setTransferWorkingHours(normalizeWorkingHours(data.transfer_working_hours));
                setSttProvider(data.stt_provider || '');
                const vv = data.variable_values || {};
                setVariableRows(Object.entries(vv).map(([k, v]) => ({ key: k, value: v })));
            }
        } catch (e) {
            setMessage({ kind: 'error', text: 'Failed to load Live Phone Call settings.' });
        } finally {
            setLoading(false);
        }
    }, [chatbotId, manualEditor]);

    const fetchPhoneProviders = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/sms/service/configs', { params: { channel: 'phone' } });
            setPhoneProviders(res?.data?.data || []);
        } catch (e) {
            /* tolerate */
        } finally {
            setProvidersLoaded(true);
        }
    }, []);

    // Timezone options for the call-transfer working hours (same seeded list the
    // User Profile page uses).
    const fetchTimezones = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/selections/timezones');
            setTimezones(res?.data?.data || []);
        } catch (e) {
            /* tolerate — the field falls back to the stored value */
        }
    }, []);

    const fetchAvailableSources = useCallback(async (page = 1) => {
        if (!chatbotId) return;
        setLoadingSources(true);
        try {
            const res = await axiosInstance.get(
                `/api/chatbots/${chatbotId}/live-phone-call/sources`,
                { params: { source_type: sourceTypes[sourceTab], page, limit: 20, search: searchText } },
            );
            setAvailableSources(res?.data?.data || []);
        } finally {
            setLoadingSources(false);
        }
    }, [chatbotId, sourceTab, searchText]);

    useEffect(() => {
        if (!checkingPermissions && canView) {
            fetchSettings();
            fetchPhoneProviders();
            fetchTimezones();
        }
    }, [checkingPermissions, canView, fetchSettings, fetchPhoneProviders, fetchTimezones]);

    useEffect(() => {
        if (!checkingPermissions && canView) fetchAvailableSources(1);
    }, [checkingPermissions, canView, sourceTab, searchText, fetchAvailableSources]);

    const toggleSource = (src) => {
        const exists = selectedSources.find(s => s.source_id === src.source_id && s.source_type === src.source_type);
        if (exists) setSelectedSources(selectedSources.filter(s => !(s.source_id === src.source_id && s.source_type === src.source_type)));
        else setSelectedSources([...selectedSources, src]);
    };

    const removeSelected = (src) => toggleSource(src);

    const addVarRow = () => setVariableRows([...variableRows, { key: '', value: '' }]);
    const updateVarRow = (i, field, val) => {
        const next = [...variableRows];
        next[i] = { ...next[i], [field]: val };
        setVariableRows(next);
    };
    const removeVarRow = (i) => setVariableRows(variableRows.filter((_, idx) => idx !== i));

    const updateWorkingDay = (key, field, val) =>
        setTransferWorkingHours(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }));

    const handleSave = async () => {
        // The content budget only applies in source mode — when RAG is on, the
        // (hidden) sources/manual content aren't used, so don't block the save on it.
        if (!ragEnabled && computeTotalChars() > MAX_CHARACTERS) {
            setMessage({ kind: 'error', text: `Total content exceeds ${MAX_CHARACTERS.toLocaleString()} characters.` });
            return;
        }
        // E.164 (required '+', non-zero leading digit, 7..15 digits total).
        const e164 = /^\+[1-9]\d{6,14}$/;
        const trimmedTransfer = (transferNumber || '').trim();
        if (trimmedTransfer && !e164.test(trimmedTransfer)) {
            setMessage({ kind: 'error', text: 'Transfer phone number must be in E.164 format (e.g. +12245551234).' });
            return;
        }
        if (transferEnabled && !trimmedTransfer) {
            setMessage({ kind: 'error', text: 'A transfer phone number is required when call transfer is enabled.' });
            return;
        }
        // When transfer is on, every enabled working day needs both times set
        // (the time inputs always carry a value, but guard a manually-cleared field).
        if (transferEnabled) {
            const badDay = WEEKDAYS.find(([key]) => {
                const d = transferWorkingHours[key];
                return d?.enabled && (!d.start || !d.end);
            });
            if (badDay) {
                setMessage({ kind: 'error', text: `Set an open and close time for ${badDay[1]}, or turn that day off.` });
                return;
            }
        }
        setSaving(true);
        try {
            const variable_values = {};
            variableRows.forEach(r => { if (r.key && r.key.trim()) variable_values[r.key.trim()] = r.value || ''; });
            await axiosInstance.post(`/api/chatbots/${chatbotId}/live-phone-call`, {
                rag_enabled: ragEnabled,
                greeting_message: greetingMessage,
                prompt_text: promptText,
                manual_content: manualContent,
                sources: selectedSources,
                variable_values,
                service_config_id: serviceConfigId || null,
                inbound_phone_number: inboundNumber || null,
                outbound_enabled: outboundEnabled,
                transfer_enabled: transferEnabled,
                transfer_phone_number: trimmedTransfer || null,
                transfer_timezone: transferTimezone || DEFAULT_TRANSFER_TIMEZONE,
                transfer_working_hours: transferWorkingHours,
                stt_provider: sttProvider || null,
            });
            setMessage({ kind: 'success', text: 'Live Phone Call settings saved.' });
        } catch (e) {
            const msg = e?.response?.data?.message || 'Save failed.';
            setMessage({ kind: 'error', text: msg });
        } finally {
            setSaving(false);
        }
    };

    if (checkingPermissions) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (!canView) return <Alert severity="warning" sx={{ m: 3 }}>You don't have permission to view Live Phone Call settings.</Alert>;
    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

    const totalChars = computeTotalChars();
    const pct = Math.min(100, (totalChars / MAX_CHARACTERS) * 100);

    // ---- Provider gate + step / wizard navigation + validation ----
    // A team must configure at least one phone provider before the setup steps
    // are shown. Telephony (provider + inbound) and General (greeting + prompt)
    // are required; Variables is optional.
    const hasProvider = phoneProviders.length > 0;
    const telephonyValid = !!serviceConfigId && !!(inboundNumber || '').trim();
    const generalValid = !!(greetingMessage || '').trim() && !!(promptText || '').trim();
    const mandatoryValid = telephonyValid && generalValid;
    const stepValid = (t) => (t === 0 ? telephonyValid : t === 1 ? generalValid : true);
    // A later step is only reachable once the mandatory steps before it are valid
    // (read-only users may browse freely). Keeps the tab bar in sync with the
    // disabled Next button so users can't jump past an incomplete required step.
    const canGoToStep = (i) => {
        if (!canModify) return true;
        if (i <= 0) return true;
        if (i === 1) return telephonyValid;
        return telephonyValid && generalValid;
    };

    const isLastStep = tab === STEP_LABELS.length - 1;
    const goNext = () => setTab((t) => Math.min(STEP_LABELS.length - 1, t + 1));
    const goBack = () => setTab((t) => Math.max(0, t - 1));
    const forwardBlocked = canModify && (isLastStep ? !mandatoryValid : !stepValid(tab));
    const forwardHint = !forwardBlocked
        ? null
        : isLastStep
            ? 'Complete the required fields (*) in earlier steps to finish.'
            : 'Complete the required fields (*) to continue.';

    // The forward button: "Next" on intermediate steps, "End Setup" (saves) on the last.
    const renderForward = () => (
        isLastStep ? (
            canModify ? (
                <Button
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <DoneIcon />}
                    onClick={handleSave}
                    disabled={saving || !mandatoryValid}
                >
                    {saving ? 'Saving…' : 'End Setup'}
                </Button>
            ) : null
        ) : (
            <Button
                variant="contained"
                size="small"
                endIcon={<NextIcon />}
                onClick={goNext}
                disabled={canModify && !stepValid(tab)}
            >
                Next
            </Button>
        )
    );

    const stepNavTop = (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                        Step {tab + 1} of {STEP_LABELS.length}
                    </Typography>
                    {forwardHint && (
                        <Typography variant="caption" color="warning.main">{forwardHint}</Typography>
                    )}
                </Box>
                {renderForward()}
            </Box>
            <Divider sx={{ mb: 2 }} />
        </>
    );

    const stepNavBottom = (
        <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Button variant="text" size="small" startIcon={<BackIcon />} disabled={tab === 0} onClick={goBack}>
                    Back
                </Button>
                {renderForward()}
            </Box>
        </>
    );

    return (
        <Box sx={{ maxWidth: 980, mx: 'auto', pb: 2 }}>
            {/* Header + step tabs (tabs only show once a phone provider exists) */}
            <Box
                sx={{
                    mb: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 1.5, sm: 2 }, py: 1.25, flexWrap: 'wrap' }}>
                    <Box
                        sx={{
                            width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                            bgcolor: 'rgba(41,121,255,0.12)', color: 'primary.main',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <PhoneIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                            Live Phone Call
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Real telephony configuration
                        </Typography>
                    </Box>
                    <Button variant="outlined" size="small" startIcon={<ProviderIcon />} onClick={() => navigate(`${scope}/phone-providers`)}>
                        Providers
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<ToolIcon />} onClick={() => navigate(`${scope}/tool-call`)}>
                        Tools
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<LogsIcon />} onClick={() => navigate(`${scope}/call-logs?channel=phone`)}>
                        Logs
                    </Button>
                    <Button variant="outlined" size="small" color="error" startIcon={<RecordIcon />} onClick={() => navigate(`${scope}/live-phone-call/recordings`)}>
                        Recordings
                    </Button>
                    {canModify && hasProvider && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={saving || !mandatoryValid}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    )}
                </Box>
                {hasProvider && (
                    <>
                        <Divider />
                        <Tabs
                            value={tab}
                            onChange={(e, v) => setTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ px: 1, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 } }}
                        >
                            <Tab label="1. Telephony" />
                            <Tab label="2. General" disabled={!canGoToStep(1)} />
                            <Tab label="3. Knowledge & sources" disabled={!canGoToStep(2)} />
                            <Tab label="4. Variables" disabled={!canGoToStep(3)} />
                        </Tabs>
                    </>
                )}
            </Box>

            {message && (
                <Alert severity={message.kind} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            {!providersLoaded ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : !hasProvider ? (
                // Onboarding gate: no telephony provider configured yet.
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ textAlign: 'center', py: 5 }}>
                        <Box
                            sx={{
                                width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
                                bgcolor: 'rgba(41,121,255,0.12)', color: 'primary.main',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <ProviderIcon />
                        </Box>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                            Set up a phone provider to begin
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', mb: 3 }}>
                            Live Phone Call needs a connected telephony provider (Twilio, Vonage, or
                            MessageBird) before you can configure call settings. Add one to unlock the setup steps.
                        </Typography>
                        {canModify && (
                            <Button variant="contained" startIcon={<ProviderIcon />} onClick={() => navigate(`${scope}/phone-providers`)}>
                                Set up phone provider
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Step 1 — Telephony: provider + inbound + outbound, and human-handoff transfer */}
                    {tab === 0 && (
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                {stepNavTop}

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                    <FormControl fullWidth size="small" required disabled={!canModify}>
                                        <InputLabel id="phone-provider-label">Phone Provider</InputLabel>
                                        <Select
                                            labelId="phone-provider-label"
                                            value={serviceConfigId || ''}
                                            label="Phone Provider"
                                            onChange={(e) => setServiceConfigId(e.target.value)}
                                        >
                                            <MenuItem value=""><em>None (configure first)</em></MenuItem>
                                            {phoneProviders.map(p => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {p.service_type} {p.channels?.includes('sms') ? '(shared with SMS)' : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        required
                                        label="Inbound number (E.164)"
                                        placeholder="+12245551234"
                                        value={inboundNumber}
                                        onChange={(e) => setInboundNumber(e.target.value)}
                                        disabled={!canModify}
                                    />
                                </Box>
                                <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={<Switch checked={outboundEnabled} onChange={(e) => setOutboundEnabled(e.target.checked)} disabled={!canModify} />}
                                    label="Enable outbound calls"
                                />

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: transferEnabled ? 1.5 : 0 }}>
                                    <Box sx={{ flex: 1, minWidth: 220 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>Call transfer (human handoff)</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Cold-transfer to a real number when the AI can't answer or the caller asks for a human. Twilio only.
                                        </Typography>
                                    </Box>
                                    <FormControlLabel
                                        control={<Switch checked={transferEnabled} onChange={(e) => setTransferEnabled(e.target.checked)} disabled={!canModify} />}
                                        label="Enable"
                                    />
                                </Box>
                                {transferEnabled && (
                                    <Box>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Transfer number (E.164)"
                                            placeholder="+12245551234"
                                            value={transferNumber}
                                            onChange={(e) => setTransferNumber(e.target.value)}
                                            disabled={!canModify}
                                            required
                                        />

                                        {/* Working hours: the transfer only fires inside these
                                            windows, evaluated in the selected timezone. */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                            <Typography variant="subtitle2" fontWeight={600}>Working hours</Typography>
                                            <FormControl size="small" sx={{ minWidth: 220 }} disabled={!canModify}>
                                                <InputLabel id="transfer-tz-label">Timezone</InputLabel>
                                                <Select
                                                    labelId="transfer-tz-label"
                                                    value={transferTimezone}
                                                    label="Timezone"
                                                    onChange={(e) => setTransferTimezone(e.target.value)}
                                                >
                                                    {transferTimezone && !timezones.some(tz => tz.value === transferTimezone) && (
                                                        <MenuItem value={transferTimezone}>{transferTimezone}</MenuItem>
                                                    )}
                                                    {timezones.map(tz => (
                                                        <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            The call is transferred only during these hours. Outside them, the AI keeps handling the call.
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {WEEKDAYS.map(([key, label]) => {
                                                const day = transferWorkingHours[key] || {};
                                                return (
                                                    <Box
                                                        key={key}
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: { xs: '1fr', sm: '150px 1fr 1fr' },
                                                            alignItems: 'center',
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <FormControlLabel
                                                            sx={{ m: 0 }}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={!!day.enabled}
                                                                    onChange={(e) => updateWorkingDay(key, 'enabled', e.target.checked)}
                                                                    disabled={!canModify}
                                                                />
                                                            }
                                                            label={label}
                                                        />
                                                        <TextField
                                                            type="time"
                                                            size="small"
                                                            label="Open"
                                                            value={day.start || '09:00'}
                                                            onChange={(e) => updateWorkingDay(key, 'start', e.target.value)}
                                                            disabled={!canModify || !day.enabled}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                        <TextField
                                                            type="time"
                                                            size="small"
                                                            label="Close"
                                                            value={day.end || '17:00'}
                                                            onChange={(e) => updateWorkingDay(key, 'end', e.target.value)}
                                                            disabled={!canModify || !day.enabled}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}

                                {stepNavBottom}
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 2 — General: voice provider + greeting + prompt */}
                    {tab === 1 && (
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                {stepNavTop}

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                    <FormControl fullWidth size="small" disabled={!canModify}>
                                        <InputLabel id="phone-stt-provider-label">Voice Provider</InputLabel>
                                        <Select
                                            labelId="phone-stt-provider-label"
                                            value={sttProvider}
                                            label="Voice Provider"
                                            onChange={(e) => setSttProvider(e.target.value)}
                                        >
                                            <MenuItem value=""><em>Use default (server setting)</em></MenuItem>
                                            <MenuItem value="google">Google Cloud TTS</MenuItem>
                                            <MenuItem value="gemini">Gemini LLM</MenuItem>
                                            <MenuItem value="live">Gemini Live</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        required
                                        label="Greeting message"
                                        value={greetingMessage}
                                        onChange={(e) => setGreetingMessage(e.target.value)}
                                        disabled={!canModify}
                                        helperText="Spoken via TTS when the call connects"
                                    />
                                </Box>

                                <TextField
                                    sx={{ mt: 2 }}
                                    size="small"
                                    fullWidth
                                    required
                                    multiline
                                    minRows={4}
                                    maxRows={12}
                                    label="Prompt instructions"
                                    value={promptText}
                                    onChange={(e) => setPromptText(e.target.value)}
                                    disabled={!canModify}
                                    helperText={<span>Use <code>{'{{variable}}'}</code> placeholders from the Variables step.</span>}
                                />

                                {stepNavBottom}
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 3 — Knowledge & sources: RAG toggle gates the source picker. */}
                    {tab === 2 && (
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                {stepNavTop}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>Default knowledgebase (RAG)</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {ragEnabled
                                                ? 'On — answers from your trained data. Turn off to use the custom sources below.'
                                                : 'Off — uses only the sources & manual content below.'}
                                        </Typography>
                                    </Box>
                                    <Switch checked={ragEnabled} onChange={(e) => setRagEnabled(e.target.checked)} disabled={!canModify} color="primary" />
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {ragEnabled ? (
                                    <Alert severity="info" variant="outlined">
                                        Custom sources & manual content are used only when the default knowledgebase (RAG)
                                        is off. Turn it off above to configure them.
                                    </Alert>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">Content budget</Typography>
                                            <Typography
                                                variant="caption"
                                                color={totalChars > MAX_CHARACTERS ? 'error' : 'text.secondary'}
                                                fontWeight={600}
                                            >
                                                {totalChars.toLocaleString()} / {MAX_CHARACTERS.toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={pct}
                                            color={pct >= 100 ? 'error' : 'primary'}
                                            sx={{ height: 6, borderRadius: 3, mb: 2 }}
                                        />

                                        {/* Selected sources */}
                                        {selectedSources.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                {selectedSources.map(s => (
                                                    <Chip
                                                        key={`${s.source_type}-${s.source_id}`}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        label={`${s.source_title} (${(s.character_count || 0).toLocaleString()})`}
                                                        onDelete={canModify ? () => removeSelected(s) : undefined}
                                                    />
                                                ))}
                                            </Box>
                                        )}

                                        <Tabs value={sourceTab} onChange={(_, v) => setSourceTab(v)} variant="fullWidth" sx={{ mb: 1.5, minHeight: 40 }}>
                                            {sourceTabLabels.map((l, i) => <Tab key={l} label={l} icon={sourceTabIcons[i]} iconPosition="start" sx={{ minHeight: 40 }} />)}
                                        </Tabs>

                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Search…"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            sx={{ mb: 1.5 }}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                                        />

                                        {loadingSources ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
                                        ) : availableSources.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                                No trained sources for this type.
                                            </Typography>
                                        ) : (
                                            <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                {availableSources.map(src => {
                                                    const checked = !!selectedSources.find(s => s.source_id === src.source_id && s.source_type === src.source_type);
                                                    return (
                                                        <Box
                                                            key={`${src.source_type}-${src.source_id}`}
                                                            onClick={() => canModify && toggleSource(src)}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                px: 1,
                                                                py: 0.75,
                                                                borderBottom: '1px solid',
                                                                borderColor: 'divider',
                                                                '&:last-of-type': { borderBottom: 'none' },
                                                                backgroundColor: checked ? 'action.selected' : 'transparent',
                                                                '&:hover': { backgroundColor: 'action.hover' },
                                                                cursor: canModify ? 'pointer' : 'default',
                                                            }}
                                                        >
                                                            <Checkbox checked={checked} onChange={() => toggleSource(src)} disabled={!canModify} size="small" />
                                                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={src.source_title}>{src.source_title}</Typography>
                                                            <Chip size="small" variant="outlined" label={`${(src.character_count || 0).toLocaleString()}`} sx={{ ml: 1 }} />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}

                                        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Manual content</Typography>
                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                            <TiptapEditor editor={manualEditor} />
                                        </Box>
                                    </>
                                )}

                                {stepNavBottom}
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 4 — Variables */}
                    {tab === 3 && (
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                {stepNavTop}

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <VarIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="subtitle2" fontWeight={600}>Variables</Typography>
                                    <Box sx={{ flex: 1 }} />
                                    {canModify && <Button size="small" startIcon={<AddIcon />} onClick={addVarRow}>Add</Button>}
                                </Box>
                                {variableRows.length === 0 ? (
                                    <Typography variant="caption" color="text.disabled">No variables defined.</Typography>
                                ) : variableRows.map((r, i) => (
                                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 1, mb: 1 }}>
                                        <TextField size="small" label="Name" value={r.key} onChange={(e) => updateVarRow(i, 'key', e.target.value)} disabled={!canModify} />
                                        <TextField size="small" label="Value" value={r.value} onChange={(e) => updateVarRow(i, 'value', e.target.value)} disabled={!canModify} />
                                        <IconButton onClick={() => removeVarRow(i)} disabled={!canModify} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton>
                                    </Box>
                                ))}

                                {stepNavBottom}
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </Box>
    );
}
